// app/src/Shell.jsx — PWA-aware version of the Kanjo shell
//
// Same component API as the design-canvas Shell (KShell, KHeader, BottomNav,
// Chev, PersonaChip), but consults `KanjoNav` context to decide whether to
// render the device-frame simulation (canvas) or fill the real viewport (PWA).
// In PWA mode, BottomNav tabs + KHeader back-chevron drive hash navigation.

const KanjoNav = React.createContext({
  pwa: false,
  current: '/today',
  navigate: () => {},
  back: () => {},
});

// Map a tab id → its root route
const TAB_ROUTE = {
  today:       '/today',
  receipts:    '/receipts',
  buy:         '/buy',
  delivery:    '/delivery',
  maintenance: '/maintenance',
  spending:    '/spending',
  reports:     '/reports',
};

// ─── Shell ──────────────────────────────────────────────────
// Small pill that sits in the KHeader right slot (next to the avatar) when
// demo mode is on. Tapping it opens a short explainer — much quieter than the
// full-width banner this replaced.
function KDemoChip() {
  useT();
  const [on, setOn] = React.useState(() => !!(window.kanjo?.demo?.isOn?.()));
  const [openExplain, setOpenExplain] = React.useState(false);
  React.useEffect(() => {
    const refresh = () => setOn(!!(window.kanjo?.demo?.isOn?.()));
    const onVis = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('storage', refresh);
    refresh();
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('storage', refresh); };
  }, []);
  if (!on) return null;
  return (
    <>
      <button onClick={() => setOpenExplain(true)} aria-label={t('demo.chip', 'Demo data')}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '4px 9px 4px 8px', borderRadius: 999,
          background: 'var(--brand-soft,#eef3ff)', color: 'var(--brand,#3a5fbf)',
          border: '1px solid var(--brand-soft-rule,rgba(91,141,239,0.25))',
          fontSize: 11, fontWeight: 600, letterSpacing: '0.01em',
          cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <span style={{ width: 6, height: 6, borderRadius: 3, background: 'currentColor' }}/>
        {t('demo.chip', 'Demo data')}
      </button>
      <Sheet open={openExplain} onClose={() => setOpenExplain(false)} title={t('demo.title', 'Demo data')}>
        <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5 }}>
          {t('demo.body')}
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 14 }}>
          {t('demo.howOff', 'Turn it off from')} <span style={{ color: 'var(--ink-2)', fontWeight: 600 }}>{t('demo.adminPath', 'Account → Admin')}</span>.
        </div>
      </Sheet>
    </>
  );
}

// Quiet chip in the header right cluster for the read-only state: a viewer
// (team member of someone else's account). Replaces the previous full-width
// banner so the page title isn't competing with a permanent warning strip.
function KReadOnlyChip() {
  const acct = window.kanjo && window.kanjo.scale && window.kanjo.scale.account;
  const [mode, setMode] = React.useState(() => (acct?.currentAccountSync?.()?.role === 'viewer') ? 'viewer' : null);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    let live = true;
    acct?.currentAccount?.().then((a) => {
      if (!live) return;
      setMode(a?.role === 'viewer' ? 'viewer' : null);
    }).catch(() => {});
    return () => { live = false; };
  }, []);
  if (!mode) return null;
  return (
    <>
      <button onClick={() => setOpen(true)} aria-label="Read-only mode — what's this?"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5,
          padding: '4px 9px 4px 8px', borderRadius: 999,
          background: 'var(--warning-soft,#fff4e0)', color: 'var(--warning,#9a6700)',
          border: '1px solid rgba(154,103,0,0.25)',
          fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
        <span style={{ width: 6, height: 6, borderRadius: 3, background: 'currentColor' }}/>
        Read-only
      </button>
      <Sheet open={open} onClose={() => setOpen(false)} title="Read-only">
        <div style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.5 }}>
          You're viewing the account owner's books. Only the owner can change settings or upload data.
        </div>
      </Sheet>
    </>
  );
}

function KShell({ children, dark = false, time = '8:47', tab = 'today', persona = 'owner', noNav = false }) {
  const nav = React.useContext(KanjoNav);

  // PWA mode — fill viewport (or center a phone-width column on wider screens),
  // real status bar, safe-area aware.
  if (nav.pwa) {
    return (
      <div style={{
        position: 'fixed', inset: 0,
        background: 'var(--bg)',
        display: 'flex', alignItems: 'stretch', justifyContent: 'center',
      }}>
        <div className={'kanjo ' + (dark ? 'kanjo-dark' : '')}
             style={{
               width: '100%', maxWidth: 480,
               background: 'var(--bg)',
               display: 'flex', flexDirection: 'column',
               overflow: 'hidden',
               boxShadow: '0 0 0 0.5px var(--rule)',
             }}>
          <div style={{
            flex: 1, minHeight: 0, overflowY: 'auto',
            paddingTop: 'env(safe-area-inset-top, 0)',
            position: 'relative',
          }}>
            <div style={{ minHeight: '100%', position: 'relative' }}>{children}</div>
          </div>
          {!noNav && <BottomNav active={tab} dark={dark} pwa />}
        </div>
      </div>
    );
  }

  // Canvas mode — device frame + simulated status bar
  const ink = dark ? '#ECEAE4' : '#0E1116';
  return (
    <div className={'kanjo ' + (dark ? 'kanjo-dark' : '')}
         style={{
           width: 390, height: 844, position: 'relative', overflow: 'hidden',
           background: 'var(--bg)',
         }}>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 54, zIndex: 30,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '17px 28px 0',
        color: ink, fontFamily: 'var(--sans)', fontWeight: 600, fontSize: 15.5,
        pointerEvents: 'none',
      }}>
        <span>{time}</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width="17" height="11" viewBox="0 0 17 11" fill={ink}>
            <rect x="0" y="7" width="3" height="4" rx="0.5"/>
            <rect x="4.5" y="5" width="3" height="6" rx="0.5"/>
            <rect x="9" y="2.5" width="3" height="8.5" rx="0.5"/>
            <rect x="13.5" y="0" width="3" height="11" rx="0.5"/>
          </svg>
          <svg width="14" height="11" viewBox="0 0 14 11" fill={ink}>
            <path d="M7 3c1.9 0 3.6.8 4.8 2L13 3.8C11.4 2.2 9.3 1.2 7 1.2S2.6 2.2 1 3.8L2.2 5c1.2-1.2 2.9-2 4.8-2zm0 3.4c1 0 1.9.4 2.5 1.1l1.3-1.3C9.7 5.3 8.4 4.7 7 4.7s-2.7.6-3.8 1.5l1.3 1.3c.6-.7 1.5-1.1 2.5-1.1z"/>
            <circle cx="7" cy="9.3" r="1.2"/>
          </svg>
          <svg width="24" height="11" viewBox="0 0 24 11" fill="none">
            <rect x="0.5" y="0.5" width="21" height="10" rx="2.5" stroke={ink} strokeOpacity="0.4"/>
            <rect x="2" y="2" width="15" height="7" rx="1" fill={ink}/>
            <path d="M22.5 4v3c.6-.2 1-.7 1-1.5s-.4-1.3-1-1.5z" fill={ink} fillOpacity="0.5"/>
          </svg>
        </div>
      </div>
      <div style={{
        position: 'absolute', top: 11, left: '50%', transform: 'translateX(-50%)',
        width: 112, height: 32, borderRadius: 24, background: '#000', zIndex: 40,
      }} />

      <div style={{
        position: 'absolute', top: 54, left: 0, right: 0,
        bottom: noNav ? 0 : 84,
        overflow: 'hidden',
      }}>
        {children}
      </div>

      {!noNav && <BottomNav active={tab} dark={dark} />}

      <div style={{
        position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)',
        width: 134, height: 5, borderRadius: 100, background: ink, opacity: 0.9, zIndex: 50,
      }} />
    </div>
  );
}

// ─── Page header ───────────────────────────────────────────
// Two modes only:
//   Root   (back === false): big left-aligned title, subtitle below, profile or
//                            other action in the right slot.
//   Detail (back === true):  back chevron left, centered title, optional right
//                            action; subtitle (if any) below, centered.
// The legacy `big` prop is accepted but ignored — every callsite ends up in one
// of these two shapes.
function KHeader({ back = true, title, subtitle, right, onBack: onBackProp, big: _big }) {
  useT(); // re-render header strings when the user switches language
  const nav = React.useContext(KanjoNav);
  // onBack lets a screen intercept the chevron (e.g. confirm before discarding
  // in-progress work); without it we fall back to plain history navigation.
  const onBack = () => { if (onBackProp) { onBackProp(); return; } nav.pwa && nav.back(); };
  // Demo chip + help button live in the header's right slot — quiet pills next
  // to whatever the screen passes as `right` (typically the profile avatar).
  // Help is hidden on the Help screen itself to avoid the round-trip loop.
  const onHelp = () => nav.navigate('/help');
  const showHelp = !((nav.current || '').startsWith('/help'));
  const rightCluster = (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
      <KReadOnlyChip/>
      <KDemoChip/>
      {showHelp && (
        <button onClick={onHelp} aria-label={t('header.help', 'Help')} title={t('header.help', 'Help')}
          style={{ width: 30, height: 30, borderRadius: 15, background: 'var(--surface,#fff)',
            border: '1px solid var(--rule)', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--ink-2)', padding: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <path d="M9.1 9a3 3 0 1 1 5.4 1.8c-.9.6-1.5 1.2-1.5 2.2"/>
            <line x1="12" y1="17" x2="12" y2="17"/>
          </svg>
        </button>
      )}
      {right || null}
    </div>
  );

  if (!back) {
    return (
      <div style={{ padding: '8px 20px 16px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.02em', lineHeight: 1.15 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 13, color: 'var(--on-dim)', marginTop: 4 }}>{subtitle}</div>}
        </div>
        {rightCluster}
      </div>
    );
  }

  return (
    <div style={{ padding: '8px 20px 12px' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 32 }}>
        <button onClick={onBack} aria-label={t('back', 'Back')} style={{
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          color: 'var(--on)', display: 'flex', alignItems: 'center', width: 28,
        }}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 5l-7 6 7 6"/>
          </svg>
        </button>
        {title && (
          <div style={{ position: 'absolute', left: 40, right: 40, textAlign: 'center',
            fontSize: 16, fontWeight: 600, pointerEvents: 'none',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
        )}
        <div style={{ minWidth: 28, display: 'flex', justifyContent: 'flex-end' }}>{rightCluster}</div>
      </div>
      {subtitle && (
        <div style={{ fontSize: 12.5, color: 'var(--on-dim)', marginTop: 6, textAlign: 'center' }}>{subtitle}</div>
      )}
    </div>
  );
}

// ─── Bottom Nav ─────────────────────────────────────────────
function BottomNav({ active, dark, pwa = false }) {
  useT(); // re-render labels on language change
  const nav = React.useContext(KanjoNav);
  // Delivery is a Pro feature — the tab only appears for entitled accounts.
  // Default hidden until the plan resolves (and keep it shown if it's the active
  // tab, so a deep link doesn't lose its own nav entry while loading).
  const [showDelivery, setShowDelivery] = React.useState(active === 'delivery');
  // Spending (card credit/debit outflows) is Pro + à-la-carte only. When shown
  // it takes the Maintenance slot; free/basic keep Maintenance instead.
  const [showSpending, setShowSpending] = React.useState(active === 'spending');
  // Free-tier filter (Phase 3): free users only see Maintenance + Reports.
  // Read tier from the JWT claim that the pre-token-generation Lambda stamps;
  // null until resolved, then 'free' | 'basic' | 'pro' | 'payg'.
  const [tier, setTier] = React.useState(null);
  React.useEffect(() => {
    let live = true;
    (async () => {
      try {
        const plan = window.kanjo?.scale?.plan;
        const p = await plan?.getPlan?.();
        const ent = plan?.entitlements?.(p);
        if (live) setShowDelivery(!!ent?.features?.delivery || active === 'delivery');
        const claims = await window.kanjo?.auth?.currentClaims?.().catch(() => null);
        const claimTier = claims ? String(claims['custom:tier'] || 'free').toLowerCase() : null;
        let groups = claims?.['cognito:groups'];
        if (typeof groups === 'string') groups = groups.replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
        const isAdmin = Array.isArray(groups) && groups.includes('admins');
        if (live) {
          setTier(claimTier);
          setShowSpending(isAdmin || claimTier === 'pro' || claimTier === 'payg' || active === 'spending');
        }
      } catch { /* stay hidden */ }
    })();
    return () => { live = false; };
  }, [active]);
  const fullItems = [
    { id: 'today',       label: t('nav.today',       'Today'),       icon: <NavIcoToday /> },
    { id: 'receipts',    label: t('nav.receipts',    'Receipts'),    icon: <NavIcoReceipts /> },
    { id: 'buy',         label: t('nav.buy',         'Buy'),         icon: <NavIcoBuy /> },
    ...(showDelivery ? [{ id: 'delivery', label: t('nav.delivery', 'Delivery'), icon: <NavIcoDelivery /> }] : []),
    // Spending replaces Maintenance for Plaid-entitled plans (Pro / à-la-carte).
    ...(showSpending
      ? [{ id: 'spending',    label: t('nav.spending',    'Spending'),    icon: <NavIcoSpending /> }]
      : [{ id: 'maintenance', label: t('nav.maintenance', 'Maintenance'), icon: <NavIcoMaintenance /> }]),
    { id: 'reports',     label: t('nav.reports',     'Reports'),     icon: <NavIcoReports /> },
  ];
  // Hold the full nav until tier resolves so a free user doesn't see a
  // flash of paid tabs. filterNavItems is a no-op for paid tiers.
  const items = tier === null
    ? fullItems
    : window.kanjo?.scale?.freeTier?.filterNavItems?.(fullItems, tier, active) || fullItems;
  return (
    <div style={{
      // PWA: docked bottom of real viewport with safe-area padding
      // Canvas: docked bottom of the 844px shell
      position: pwa ? 'sticky' : 'absolute',
      left: 0, right: 0, bottom: 0,
      paddingTop: 8,
      paddingBottom: pwa ? 'calc(env(safe-area-inset-bottom, 0) + 10px)' : 24,
      paddingLeft: 8, paddingRight: 8,
      display: 'flex',
      background: 'var(--surface)',
      borderTop: '1px solid var(--rule)',
      zIndex: 20,
      height: pwa ? undefined : 84,
    }}>
      {items.map(it => {
        const on = it.id === active;
        return (
          <button key={it.id}
            onClick={() => nav.pwa && nav.navigate(TAB_ROUTE[it.id])}
            style={{
              flex: 1,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
              background: 'transparent', border: 'none', padding: '4px 0',
              cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
            }}>
            <div style={{ color: on ? 'var(--ink)' : 'var(--ink-3)' }}>
              {React.cloneElement(it.icon, { filled: on })}
            </div>
            <div style={{
              fontSize: 10.5, fontWeight: on ? 600 : 500,
              color: on ? 'var(--ink)' : 'var(--ink-3)',
            }}>{it.label}</div>
          </button>
        );
      })}
    </div>
  );
}

// Filled-state inner strokes use --bg (the surface BEHIND the nav bar) rather
// than --surface — the nav bar itself uses --surface, so --surface on a
// --surface fill would vanish. --bg gives the inner cut-outs in both themes.
const NavIcoToday = ({ filled }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="16" height="15" rx="3" fill={filled ? 'currentColor' : 'none'}/>
    <line x1="3" y1="9" x2="19" y2="9" stroke={filled ? 'var(--bg)' : 'currentColor'}/>
    <line x1="7" y1="3" x2="7" y2="6" stroke={filled ? 'var(--bg)' : 'currentColor'}/>
    <line x1="15" y1="3" x2="15" y2="6" stroke={filled ? 'var(--bg)' : 'currentColor'}/>
  </svg>
);
const NavIcoReceipts = ({ filled }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 3v17l2.5-1.6L10 20l2.5-1.6L15 20l2-1.6V3z"/>
    <path d="M8 8h6M8 11h6M8 14h4" stroke={filled ? 'var(--bg)' : 'currentColor'}/>
  </svg>
);
const NavIcoBuy = ({ filled }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 5h2l2.2 10.4a2 2 0 002 1.6h6.6a2 2 0 002-1.6L19.5 8H7" fill={filled ? 'currentColor' : 'none'} fillOpacity={filled ? 0.18 : 1}/>
    <circle cx="9" cy="20" r="1.3" fill="currentColor"/>
    <circle cx="16" cy="20" r="1.3" fill="currentColor"/>
  </svg>
);
// Car (delivery vehicle) — windshield cut-out uses --bg in filled state.
const NavIcoDelivery = ({ filled }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 13l1.4-4.2A2 2 0 016.3 7.4h9.4a2 2 0 011.9 1.4L19 13v4.5a1 1 0 01-1 1h-1.2a1 1 0 01-1-1V17H6.2v.5a1 1 0 01-1 1H4a1 1 0 01-1-1z"/>
    <path d="M4.5 13h13" stroke={filled ? 'var(--bg)' : 'currentColor'}/>
    <circle cx="7" cy="15.4" r="0.9" fill={filled ? 'var(--bg)' : 'currentColor'} stroke="none"/>
    <circle cx="15" cy="15.4" r="0.9" fill={filled ? 'var(--bg)' : 'currentColor'} stroke="none"/>
  </svg>
);
// Wrench icon for Maintenance.
const NavIcoMaintenance = ({ filled }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.2 4.3a4 4 0 015.4 5.4l-1.7-1.7-2 2-1.7-1.7 2-2L14.2 4.3z"/>
    <path d="M13 8.2L4 17.2a1.7 1.7 0 002.4 2.4l9-9"/>
  </svg>
);
// Credit/debit card for Spending — the stripe cut-out uses --bg when filled.
const NavIcoSpending = ({ filled }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2.5" y="5" width="17" height="12" rx="2.5"/>
    <line x1="2.5" y1="9" x2="19.5" y2="9" stroke={filled ? 'var(--bg)' : 'currentColor'}/>
    <line x1="6" y1="13.5" x2="10" y2="13.5" stroke={filled ? 'var(--bg)' : 'currentColor'}/>
  </svg>
);
const NavIcoReports = ({ filled }) => (
  <svg width="22" height="22" viewBox="0 0 22 22" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="11" width="4" height="8" rx="1"/>
    <rect x="9" y="6"  width="4" height="13" rx="1"/>
    <rect x="15" y="9" width="4" height="10" rx="1"/>
  </svg>
);

const Chev = ({ size = 14, color = 'var(--ink-3)' }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 3l4 4-4 4"/>
  </svg>
);

// eslint-disable-next-line no-unused-vars
function PersonaChip({ persona = 'owner' }) {
  // Account affordance — a neutral profile glyph (the old mock persona initial
  // "M" wasn't tied to the real signed-in account). Opens the Account screen.
  const openAccount = () => { window.location.hash = '/account'; };
  return (
    <button onClick={openAccount} aria-label="Account" title="Account" style={{
      width: 28, height: 28, borderRadius: 14,
      background: 'var(--surface-3)', color: 'var(--ink-2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      border: 'none', cursor: 'pointer', padding: 0,
    }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
           strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="8" r="3.5"/>
        <path d="M5.5 20c0-3.6 3-5.5 6.5-5.5s6.5 1.9 6.5 5.5"/>
      </svg>
    </button>
  );
}

// ─── Link helper ────────────────────────────────────────────
// Use anywhere you want a "tap to navigate" affordance: tile, row, button.
// In canvas mode it's a no-op wrapper; in PWA mode it triggers hash navigation.
// Renders as a <div role="button"> to avoid <button>-in-<button> nesting when
// wrapping existing button elements.
function KLink({ to, children, asButton = false, style = {} }) {
  const nav = React.useContext(KanjoNav);
  const onClick = (e) => {
    if (!nav.pwa || !to) return;
    e.preventDefault();
    e.stopPropagation();
    nav.navigate(to);
  };
  const onKey = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && nav.pwa && to) {
      e.preventDefault();
      nav.navigate(to);
    }
  };
  if (asButton) {
    return (
      <div role="button" tabIndex={nav.pwa ? 0 : -1}
           onClick={onClick} onKeyDown={onKey}
           style={{ cursor: nav.pwa ? 'pointer' : 'default', display: 'block', ...style }}>
        {children}
      </div>
    );
  }
  return (
    <a href={to ? '#' + to : '#'} onClick={onClick}
       style={{ color: 'inherit', textDecoration: 'none', cursor: nav.pwa ? 'pointer' : 'default', display: 'block', ...style }}>
      {children}
    </a>
  );
}

Object.assign(window, { KShell, KHeader, BottomNav, Chev, PersonaChip, KanjoNav, KLink });
